/**
 * The volume picker's form state and the request it turns into.
 *
 * Kept beside the steps rather than inside them because three components and the wizard itself all
 * read the same form state, and because the shape the API wants is not the shape a form is
 * comfortable holding: the wire type carries decimals as strings and an unrestricted dimension as
 * an empty list, while the form holds numbers and nulls.
 */

import { STANDARD_AIB_GC_COUNTRIES } from "@/constants";
import { addMonths, format, isValid, parseISO, subYears } from "date-fns";
import { isEqual } from "lodash-es";
import type {
  EligibilityCriteriaRequest,
  PickerConfirmRequest,
  PickerQuery,
} from "@/forwards2/forwardsTypes";
import {
  currentQuarterLabel,
  formatPeriodLabel,
  HALF_YEARS_PER_YEAR,
  MONTHS_PER_YEAR,
  nextPeriodLabel,
  parsePeriodLabel,
  periodLastDay,
  shiftQuarterLabel,
} from "@/forwards2/periodUtils";
import {
  FORWARD_ELIGIBLE_TECHNOLOGIES,
  technologyPrefix,
} from "@/types/technology";

/** `constants.DEAL_NAME_MAX` — a product bound: the name titles the deal page and the offering sheet. */
export const NAME_MAX = 128;
/** `constants.DEAL_INTERNAL_NOTES_MAX`. */
export const INTERNAL_NOTES_MAX = 20000;

/** The COD calculator's default limit on a device's age at delivery (user, 2026-10-23). */
export const DEFAULT_MAX_COD_AGE_YEARS = 15;

/**
 * The bundle a buyer means by "HSW" — `deal_picker_service.HSW_ENERGY_TYPES`, as AIB code prefixes.
 *
 * The technology criterion travels as prefixes and never as energy-type keys: the backend matches
 * it against `registry_device.technology_code` by prefix, which no key ever matches (RV-picker-01).
 */
export const HSW_TECHNOLOGIES: readonly string[] =
  FORWARD_ELIGIBLE_TECHNOLOGIES.map(technologyPrefix);

/** The two technologies that rarely sell inside HSW — `deal_picker_service.ADVANCED_ENERGY_TYPES`. */
export const ADVANCED_TECHNOLOGIES: readonly string[] = [
  technologyPrefix("biomass"),
  technologyPrefix("nuclear"),
];

/** Whether a country criterion is exactly the Standard AIB GC set, in any order. */
export function isStandardAibGc(countries: readonly string[]): boolean {
  return isEqual([...countries].sort(), [...STANDARD_AIB_GC_COUNTRIES].sort());
}

/** The Standard AIB GC chip: on selects exactly that set, off clears the field (unrestricted). */
export function toggledStandardAibGc(countries: readonly string[]): string[] {
  return isStandardAibGc(countries) ? [] : [...STANDARD_AIB_GC_COUNTRIES];
}

export type TechnologyPresetKey = "hsw" | "biomass" | "nuclear";

/** The quick chips of the spot sale's table, as criteria instead of column filters. */
export const TECHNOLOGY_PRESETS: readonly {
  key: TechnologyPresetKey;
  technologies: readonly string[];
}[] = [
  { key: "hsw", technologies: HSW_TECHNOLOGIES },
  { key: "biomass", technologies: [technologyPrefix("biomass")] },
  { key: "nuclear", technologies: [technologyPrefix("nuclear")] },
];

/**
 * The deal's own name, description and internal notes: what the deal is called, never what serves it.
 * The description is invoice text; the notes are staff-only.
 */
export interface DealIdentityForm {
  name: string;
  description: string;
  internal_notes: string;
}

/**
 * The eligibility criteria as the form holds them — `EligibilityCriteriaRequest` with an unset date
 * as "" rather than null, which is what a date input binds to.
 *
 * Updates replace the arrays rather than mutate them: the accepted query shares them with the form.
 */
export interface EligibilityCriteriaForm {
  countries: string[];
  technologies: string[];
  cod_from: string;
  cod_until: string;
  support: boolean | null;
  ecolabels: string[];
}

/**
 * Step two's state: the first and last quarter of the range, both inclusive. No width is picked: each
 * mandate covers the range at its own (`deal_picker_service._cover`).
 */
export interface PickerPeriodForm {
  from: string;
  until: string;
}

export function emptyDealIdentityForm(): DealIdentityForm {
  return { name: "", description: "", internal_notes: "" };
}

/** The criteria the picker opens on: none, so nothing is restricted until a filter is chosen. */
export function emptyCriteriaForm(): EligibilityCriteriaForm {
  return {
    countries: [],
    technologies: [],
    cod_from: "",
    cod_until: "",
    support: null,
    ecolabels: [],
  };
}

/**
 * The default range: all four quarters of the next calendar year, since most contracts sell each quarter's
 * production as its own vintage (user, 2026-10-07).
 */
export function defaultPeriodForm(at: Date = new Date()): PickerPeriodForm {
  const year = at.getFullYear() + 1;
  return {
    from: formatPeriodLabel({ width: "quarter", year, quarter: 1 }),
    until: formatPeriodLabel({ width: "quarter", year, quarter: 4 }),
  };
}

/**
 * The quarters the range selects offer: a little history for a late sale, and enough of the future
 * to sell forward.
 */
export function periodOptions(at: Date = new Date()): string[] {
  const start = shiftQuarterLabel(currentQuarterLabel(at), -PAST_QUARTERS);
  return Array.from(
    { length: PAST_QUARTERS + FUTURE_QUARTERS + 1 },
    (_unused, index) => shiftQuarterLabel(start, index)
  );
}

/**
 * The period `delta` steps from `label`, in the label's own width.
 *
 * @returns the label, or null when the argument is not a period label.
 */
export function shiftPeriodLabel(label: string, delta: number): string | null {
  const parsed = parsePeriodLabel(label);
  if (!parsed) return null;

  if (parsed.width === "year") {
    return formatPeriodLabel({ width: "year", year: parsed.year + delta });
  }
  if (parsed.width === "half_year") {
    const zeroBased =
      parsed.year * HALF_YEARS_PER_YEAR + (parsed.halfYear! - 1) + delta;
    return formatPeriodLabel({
      width: "half_year",
      year: Math.floor(zeroBased / HALF_YEARS_PER_YEAR),
      halfYear:
        (((zeroBased % HALF_YEARS_PER_YEAR) + HALF_YEARS_PER_YEAR) %
          HALF_YEARS_PER_YEAR) +
        1,
    });
  }
  if (parsed.width === "quarter") return shiftQuarterLabel(label, delta);

  const zeroBased = parsed.year * MONTHS_PER_YEAR + (parsed.month! - 1) + delta;
  return formatPeriodLabel({
    width: "month",
    year: Math.floor(zeroBased / MONTHS_PER_YEAR),
    month:
      (((zeroBased % MONTHS_PER_YEAR) + MONTHS_PER_YEAR) % MONTHS_PER_YEAR) + 1,
  });
}

/** Every period label from `from` to `until` inclusive; empty when the range runs backwards. */
export function periodRange(form: PickerPeriodForm): string[] {
  if (form.from === "" || form.until === "") return [];

  const labels: string[] = [];
  let cursor = form.from;
  // A range is at most a few dozen periods, but a malformed pair of labels must not spin forever.
  for (let step = 0; step < MAX_RANGE_LENGTH; step += 1) {
    labels.push(cursor);
    if (cursor === form.until) return labels;
    const next = nextPeriodLabel(cursor);
    if (next === null) return [];
    cursor = next;
  }
  return [];
}

/**
 * The COD calculator's default delivery date: a month after the range's last production quarter ends
 * (user, 2026-10-23).
 *
 * @returns `YYYY-MM-DD`, or "" when `until` is not a period label.
 */
export function defaultCodDeliveryDate(until: string): string {
  const lastDay = periodLastDay(until);
  if (lastDay === null) return "";
  return format(addMonths(parseISO(lastDay), 1), ISO_DATE);
}

/**
 * The earliest COD a device may have to be at most `maxAgeYears` old on `deliveryDate`.
 *
 * @returns `YYYY-MM-DD`, or null when the date or the age is not usable.
 */
export function codFromForMaxAge(
  deliveryDate: string,
  maxAgeYears: number | null
): string | null {
  const delivery = parseISO(deliveryDate);
  if (!isValid(delivery) || maxAgeYears === null || maxAgeYears < 0) {
    return null;
  }
  return format(subYears(delivery, maxAgeYears), ISO_DATE);
}

/** The criteria as the API takes them: dates and lists, with "" standing for unrestricted. */
export function criteriaRequest(
  form: EligibilityCriteriaForm
): EligibilityCriteriaRequest {
  return {
    countries: form.countries,
    technologies: form.technologies,
    cod_from: form.cod_from === "" ? null : form.cod_from,
    cod_until: form.cod_until === "" ? null : form.cod_until,
    support: form.support,
    ecolabels: form.ecolabels,
  };
}

/**
 * The take table's query for the current form state and selection. `minPriceFilter` narrows the rows
 * on screen and never reaches the created deal.
 */
export function pickerQuery(
  criteria: EligibilityCriteriaForm,
  minPriceFilter: number | null,
  periods: string[],
  takenMandateIds: number[]
): PickerQuery {
  return {
    periods,
    criteria: criteriaRequest(criteria),
    min_price_filter: minPriceFilter === null ? null : String(minPriceFilter),
    taken_mandate_ids: takenMandateIds,
  };
}

/**
 * The confirm body. Non-positive takes are dropped here as well as on the backend.
 *
 * `criteria` is the query the take table answered rather than the form, so a criterion edited since
 * cannot reach a deal whose takes were chosen against the old answer.
 */
export function confirmRequest(
  identity: DealIdentityForm,
  criteria: EligibilityCriteriaRequest,
  periods: string[],
  takes: Readonly<Record<number, number>>
): PickerConfirmRequest {
  const positive: Record<number, number> = {};
  for (const [mandateId, volume] of Object.entries(takes)) {
    if (volume > 0) positive[Number(mandateId)] = volume;
  }

  return {
    name: identity.name.trim(),
    description: identity.description.trim() || null,
    internal_notes: identity.internal_notes.trim() || null,
    criteria,
    periods,
    takes: positive,
  };
}

/** A range holds at most this many periods; a malformed pair of labels must not spin forever. */
const ISO_DATE = "yyyy-MM-dd";
const MAX_RANGE_LENGTH = 120;

const PAST_QUARTERS = 4;
const FUTURE_QUARTERS = 16;
